import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../store";
import {
  getUserDetails,
  updateUserDetails,
  deleteUser,
  updateUserStatus,
  completeUserOnboarding as completeUserOnboardingApi,
  updateUserFavourites,
  deleteUserFavourites,
  getUserFavourites,
  deleteUserProfilePic,
} from "../../services/userService";
import {
  UserDetailsFetchRequest,
  User,
  UserDetailsUpdateRequest,
  UserUpdateDetails,
  UserStatusUpdateRequest,
  UserFavouritesUpdateRequest,
  UserFavouritesFetchRequest,
  UserFavouritesItem,
  ApiResponse,
  UserDeleteData,
  UserOnboardingData,
} from "../../types";
import { ERROR_MESSAGES } from "../../utils/constants";
import { extractApiErrorMessage } from "../../helpers/apiHelper";
import { resolveLocation } from "../../utils/locationHelper";
import { toUserStatus } from "../../utils/userOnboarding";

interface UserState {
  currentUserId?: string | null;
  userProfile?: User | null;
  vendorProfile?: User | null;
  currentUserFavourites: UserFavouritesItem[] | null;
  loading: boolean;
  error: string | null;
}

const initialState: UserState = {
  currentUserId: null,
  userProfile: null,
  vendorProfile: null,
  loading: false,
  error: null,
  currentUserFavourites: null,
};

export const fetchUserDetails = createAsyncThunk(
  "user/fetchUserDetails",
  async (data: UserDetailsFetchRequest, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const locationState = state.location.location;
      const resolvedLoc = resolveLocation({
        lat: data.lat,
        lon: data.lon,
        locationState,
        allowNull: true,
      });
      if (resolvedLoc.error) {
        return rejectWithValue(resolvedLoc.error);
      }
      const requestData: UserDetailsFetchRequest = {
        userId: data.userId,
        lat: resolvedLoc.lat,
        lon: resolvedLoc.lon,
      };
      const response = await getUserDetails(requestData);
      const user = response as unknown as User;
      const loggedInUserId = state.auth.userId;
      return {
        user,
        isCurrentUser: user.userId === loggedInUserId,
      };
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        ERROR_MESSAGES.FAILED_TO_FETCH_USER_DETAILS;
      return rejectWithValue({ message: errorMessage, originalError: error });
    }
  }
);

export const updateUserDetailsInfo = createAsyncThunk(
  "user/updateUserDetails",
  async (data: UserDetailsUpdateRequest) => {
    try {
      const response = await updateUserDetails(data);
      return response as unknown as UserUpdateDetails;
    } catch (error: any) {
      throw error;
    }
  }
);

export const removeUser = createAsyncThunk<
  UserDeleteData,
  string,
  { rejectValue: string }
>("user/removeUser", async (userId, { rejectWithValue }) => {
  try {
    return await deleteUser(userId);
  } catch (error: unknown) {
    const err = error as {
      response?: { data?: { message?: string } };
      message?: string;
    };
    return rejectWithValue(
      err.response?.data?.message ||
        err.message ||
        ERROR_MESSAGES.FAILED_TO_DELETE_USER
    );
  }
});

export const completeUserOnboarding = createAsyncThunk<
  UserOnboardingData,
  { userId: string; ageConfirmed: boolean; dob?: string },
  { rejectValue: { message: string; statusCode?: number } }
>("user/completeUserOnboarding", async ({ userId, ageConfirmed, dob }, { rejectWithValue }) => {
  try {
    const body: { ageConfirmed: boolean; dob?: string } = { ageConfirmed };
    if (dob) {
      body.dob = dob;
    }
    const response = await completeUserOnboardingApi(userId, body);
    if (response?.status === "ERROR") {
      return rejectWithValue({
        message:
          response.message || ERROR_MESSAGES.ONBOARDING.FAILED_TO_COMPLETE_ONBOARDING,
        statusCode: response.statusCode,
      });
    }
    return (response?.data ?? {}) as UserOnboardingData;
  } catch (error: unknown) {
    const err = error as {
      response?: { data?: { message?: string; statusCode?: number }; status?: number };
    };
    return rejectWithValue({
      message: extractApiErrorMessage(
        error,
        ERROR_MESSAGES.ONBOARDING.FAILED_TO_COMPLETE_ONBOARDING
      ),
      statusCode:
        err.response?.data?.statusCode ?? err.response?.status,
    });
  }
});

export const changeUserStatus = createAsyncThunk<
  ApiResponse,
  UserStatusUpdateRequest,
  { rejectValue: string }
>("user/changeUserStatus", async (data, { rejectWithValue }) => {
  try {
    return await updateUserStatus(data);
  } catch (error: unknown) {
    return rejectWithValue(
      extractApiErrorMessage(
        error,
        ERROR_MESSAGES.FAILED_TO_UPDATE_USER_STATUS
      )
    );
  }
});

export const updateUserFavouritesList = createAsyncThunk(
  "user/updateUserFavouritesList",
  async (data: UserFavouritesUpdateRequest) => {
    try {
      const response = await updateUserFavourites(data);
      return response;
    } catch (error: any) {
      return error;
    }
  }
);

export const deleteUserFavouritesItem = createAsyncThunk(
  "user/deleteItemFromUserFavourites",
  async (data: UserFavouritesUpdateRequest, { rejectWithValue }) => {
    try {
      const response = await deleteUserFavourites(data);
      if (typeof response === "object" && response !== null && "status" in response) {
        if (response.status === "SUCCESS") {
          return response;
        } else {
          return rejectWithValue(
            response.message || ERROR_MESSAGES.FAILED_TO_DELETE_USER_FAVOURITES
          );
        }
      }
      if (response === true) {
        return {
          status: "SUCCESS",
          statusCode: 201,
          message: "Item removed from favourites",
          timestamp: new Date().toISOString(),
          data: true,
        } as ApiResponse;
      }
      return rejectWithValue(ERROR_MESSAGES.FAILED_TO_DELETE_USER_FAVOURITES);
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data ||
        error?.message ||
        ERROR_MESSAGES.FAILED_TO_DELETE_USER_FAVOURITES
      );
    }
  }
);

export const fetchUserFavouritesList = createAsyncThunk(
  "user/fetchUserFavouritesList",
  async (data: UserFavouritesFetchRequest, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const locationState = state.location.location;

      const resolvedLoc = resolveLocation({
        lat: data.lat,
        lon: data.lon,
        locationState,
        allowNull: true,
      });
      if (resolvedLoc.error) {
        return rejectWithValue(resolvedLoc.error);
      }
      const requestData: UserFavouritesFetchRequest = {
        userId: data.userId,
        lat: resolvedLoc.lat,
        lon: resolvedLoc.lon,
      };
      const response = await getUserFavourites(requestData);
      if (Array.isArray(response)) {
        return response as UserFavouritesItem[];
      }
      return [] as UserFavouritesItem[];
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || error?.message || ERROR_MESSAGES.FAILED_TO_FETCH_USER_FAVOURITES
      );
    }
  }
);

export const clearUserProfilePic = createAsyncThunk(
  "user/clearUserProfilePic",
  async (userId: string) => {
    try {
      const response = await deleteUserProfilePic(userId);
      return response;
    } catch (error: any) {
      return error;
    }
  }
);

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setCurrentUser(state, action: PayloadAction<User>) {
      state.currentUserId = action.payload.userId;
    },
    clearCurrentUser(state) {
      state.currentUserId = null;
      state.userProfile = null;
      state.vendorProfile = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserDetails.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchUserDetails.fulfilled,
        (
          state,
          action: PayloadAction<{ user: User; isCurrentUser: boolean }>
        ) => {
          state.loading = false;
          const { user, isCurrentUser } = action.payload;

          if (isCurrentUser) {
            state.currentUserId = user.userId;
            state.userProfile = user;
            state.vendorProfile = null;
          } else {
            state.currentUserId = user.userId;
            state.vendorProfile = user;
          }
        }
      )
      .addCase(fetchUserDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch user details";
      })
      .addCase(updateUserDetailsInfo.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        updateUserDetailsInfo.fulfilled,
        (state, action: PayloadAction<UserUpdateDetails>) => {
          state.loading = false;
          if (state.userProfile) {
            const { image, ...restPayload } = action.payload;
            const updatePayload: Partial<User> = {
              ...(restPayload as Partial<User>),
            };
            if (image && typeof image === "string") {
              updatePayload.image = image;
            }
            state.userProfile = {
              ...state.userProfile,
              ...updatePayload,
            };
          }
        }
      )
      .addCase(updateUserDetailsInfo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to update user details";
      })
      .addCase(removeUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(removeUser.fulfilled, (state) => {
        state.loading = false;
        state.currentUserId = null;
        state.userProfile = null;
        state.vendorProfile = null;
      })
      .addCase(removeUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to remove user";
      })
      .addCase(completeUserOnboarding.pending, (state) => {
        state.loading = true;
      })
      .addCase(completeUserOnboarding.fulfilled, (state, action) => {
        state.loading = false;
        if (state.userProfile) {
          const onboarding = action.payload;
          state.userProfile = {
            ...state.userProfile,
            ...(onboarding.userName !== undefined && {
              userName: onboarding.userName,
            }),
            ...(onboarding.email !== undefined && { email: onboarding.email }),
            ...(onboarding.mobile !== undefined && { mobile: onboarding.mobile }),
            ...(onboarding.dob !== undefined && { dob: onboarding.dob }),
            ...(onboarding.image !== undefined &&
              onboarding.image !== null && { image: onboarding.image }),
            userStatus: toUserStatus(onboarding.status),
          };
        }
      })
      .addCase(completeUserOnboarding.rejected, (state) => {
        state.loading = false;
      })
      .addCase(changeUserStatus.pending, (state) => {
        state.loading = true;
      })
      .addCase(changeUserStatus.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(changeUserStatus.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message || ERROR_MESSAGES.FAILED_TO_UPDATE_USER_STATUS;
      })
      .addCase(updateUserFavouritesList.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateUserFavouritesList.fulfilled, (state, action) => {
        state.loading = false;
        // API returns { status: "SUCCESS", data: true }, not an array
        // Don't update currentUserFavourites here - it will be updated via fetchUserDetails
        // and then UserFavourites component will re-fetch
      })
      .addCase(updateUserFavouritesList.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message || "Failed to update user favourites list";
      })
      .addCase(deleteUserFavouritesItem.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteUserFavouritesItem.fulfilled, (state, action) => {
        state.loading = false;
        const response = action.payload as ApiResponse & {
          data?: boolean;
          isLastItem?: boolean;
        };
        if (response?.status === "SUCCESS" && response?.data === true) {
          const deletedItemId = action.meta.arg.itemId;
          if (state.currentUserFavourites) {
            state.currentUserFavourites = state.currentUserFavourites.filter(
              (item) => item.itemId !== deletedItemId
            );
          }
        }
      })
      .addCase(deleteUserFavouritesItem.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) ||
          action.error.message ||
          ERROR_MESSAGES.FAILED_TO_DELETE_USER_FAVOURITES;
      })
      .addCase(
        fetchUserFavouritesList.fulfilled,
        (state, action: PayloadAction<UserFavouritesItem[]>) => {
          state.currentUserFavourites = action.payload;
        }
      )
      .addCase(fetchUserFavouritesList.rejected, (state, action) => {
        state.error =
          (action.payload as string) ||
          action.error.message ||
          ERROR_MESSAGES.FAILED_TO_FETCH_USER_FAVOURITES;
        state.currentUserFavourites = [];
      });
  },
});

export const { setCurrentUser, clearCurrentUser } = userSlice.actions;
export default userSlice.reducer;
export const currentUserIdSelector = (state: RootState) =>
  state.user.currentUserId;
export const userProfileSelector = (state: RootState) => state.user.userProfile;
export const vendorProfileSelector = (state: RootState) =>
  state.user.vendorProfile;
export const currentUserFavouritesSelector = (state: RootState) =>
  state.user.currentUserFavourites;
export const userLoadingSelector = (state: RootState) => state.user.loading;
